import { useRef, useLayoutEffect, useState, useCallback } from 'react';

const RESIZE_DEBOUNCE_MS = 100;
const FIT_PRECISION_PX = 0.25;

/**
 * Fits text within its container. By default wraps to multiple lines (readable).
 * Set preferWrap={false} to shrink font on one line (legacy behavior).
 */
export default function AutoFitText({
  children,
  className = '',
  style = {},
  minFontSize = 12,
  maxFontSize = 24,
  preferWrap = true,
  as: Tag = 'span',
}) {
  const ref = useRef(null);
  const [fontSizePx, setFontSizePx] = useState(null);
  const text = children == null ? '' : String(children);

  const measureFit = useCallback(() => {
    const el = ref.current;
    if (!el || !text) {
      setFontSizePx(null);
      return;
    }

    if (preferWrap) {
      setFontSizePx(null);
      return;
    }

    const container = el.parentElement;
    if (!container) return;

    const availableWidth = container.clientWidth;
    if (availableWidth <= 0) return;

    const max = Math.max(minFontSize, maxFontSize);
    const min = Math.min(minFontSize, max);

    el.style.whiteSpace = 'nowrap';
    el.style.overflow = 'hidden';

    el.style.fontSize = `${max}px`;
    if (el.scrollWidth <= availableWidth) {
      setFontSizePx(max);
      return;
    }

    let lo = min;
    let hi = max;
    let best = min;

    while (hi - lo > FIT_PRECISION_PX) {
      const mid = (lo + hi) / 2;
      el.style.fontSize = `${mid}px`;
      if (el.scrollWidth <= availableWidth) {
        best = mid;
        lo = mid;
      } else {
        hi = mid;
      }
    }

    setFontSizePx(best);
  }, [text, minFontSize, maxFontSize, preferWrap]);

  useLayoutEffect(() => {
    let cancelled = false;
    let debounceId;

    const runFit = () => {
      if (!cancelled) measureFit();
    };

    const scheduleFit = () => {
      clearTimeout(debounceId);
      debounceId = setTimeout(runFit, RESIZE_DEBOUNCE_MS);
    };

    runFit();

    const el = ref.current;
    const container = el?.parentElement;
    if (!container) {
      return () => {
        cancelled = true;
        clearTimeout(debounceId);
      };
    }

    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(scheduleFit);
      observer.observe(container);
    }

    window.addEventListener('resize', scheduleFit);

    return () => {
      cancelled = true;
      clearTimeout(debounceId);
      observer?.disconnect();
      window.removeEventListener('resize', scheduleFit);
    };
  }, [measureFit]);

  const wrapStyles = preferWrap
    ? {
        whiteSpace: 'normal',
        overflow: 'visible',
        overflowWrap: 'break-word',
        wordBreak: 'normal',
      }
    : {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
      };

  return (
    <Tag
      ref={ref}
      className={`${preferWrap ? 'app-readable' : ''} ${className}`.trim()}
      style={{
        ...style,
        display: 'block',
        width: '100%',
        minWidth: 0,
        ...wrapStyles,
        fontSize: fontSizePx != null ? `${fontSizePx}px` : `${maxFontSize}px`,
      }}
    >
      {text}
    </Tag>
  );
}
